import { useEffect, useCallback, useRef } from 'react';

const useVisibleItems = (): { getVisibleItems: () => Set<string>; observeItem: (element: HTMLElement | null, itemId: string) => void } => {
  const visibleItemsRef = useRef<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);

  const getVisibleItems = useCallback((): Set<string> => {
    return new Set(visibleItemsRef.current);
  }, []);

  const observeItem = useCallback((element: HTMLElement | null, itemId: string): void => {
    if (!element || !observerRef.current) return;
    element.dataset.itemId = itemId;
    observerRef.current.observe(element);
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.target.isConnected) return;
          const itemId = (entry.target as HTMLElement).dataset.itemId;
          if (entry.isIntersecting) {
            visibleItemsRef.current.add(itemId!);
          } else {
            visibleItemsRef.current.delete(itemId!);
          }
        });
      },
      { threshold: 0.5 }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return { getVisibleItems, observeItem };
};

export default useVisibleItems;
